/*
 * Global country/currency localization modal controller (Store Switcher + Currency Change).
 * Path B — Shopify-native {% form 'localization' %}. No Global-e script/key.
 * Opens from the header trigger(s) (siblings of the modal — document-delegated, D25);
 * closes on X / scrim / Escape; focus-trap via the GLOBAL createFocusTrap guard
 * (focus-trap.js is loaded globally in layout/theme.liquid — never re-loaded here).
 * Syncs the derived read-only currency display from the country <select> on change
 * (the native form posts country_code only — currency follows the selected country).
 * The localization form is a native POST+reload (no fetch), so no AbortController/
 * error-write contract applies; the aria-live span is present for markup parity only.
 */
;(function () {
  'use strict';
  /* global createFocusTrap */

  const modal = document.querySelector('[data-localization-modal]');
  if (!modal) return; // single-market: section not rendered

  const panel = modal.querySelector('[data-localization-modal-panel]');
  const closeBtn = modal.querySelector('[data-localization-modal-close]');
  const select = modal.querySelector('[data-localization-country]');
  const currencyOut = modal.querySelector('[data-localization-currency]'); // null if currency field off
  const initialCountry = select ? select.value : null; // active country before the user picks another
  let trap = null;
  let lastFocused = null;

  // The server renders every country as a real <option> (the no-JS fallback — a bare <select> with no
  // JS has no other way to offer every country). With JS running, the modal is closed until a trigger
  // is clicked, so nothing needs more than the one already-selected option until then: trim to it here,
  // restore the full markup the first time the modal opens. Leaves the no-JS path untouched — if this
  // script never runs, the options this trims are exactly the options that stay.
  let savedOptionsHtml = null;
  if (select && select.options.length > 1) {
    savedOptionsHtml = select.innerHTML;
    const keep = select.options[select.selectedIndex];
    select.innerHTML = '';
    if (keep) select.appendChild(keep);
  }

  function restoreOptions() {
    if (savedOptionsHtml === null || !select) return;
    const current = select.value;
    select.innerHTML = savedOptionsHtml;
    select.value = current;
    savedOptionsHtml = null;
  }

  function openModal() {
    restoreOptions();
    lastFocused = document.activeElement;
    modal.setAttribute('data-localization-modal-open', 'true');
    modal.removeAttribute('aria-hidden');
    modal.removeAttribute('inert');
    if (!trap && typeof createFocusTrap === 'function') trap = createFocusTrap(panel);
    if (trap) trap.activate();
    (select || panel).focus();
  }

  function closeModal() {
    modal.removeAttribute('data-localization-modal-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    if (trap) trap.deactivate();
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  // Derived currency display: mirror the selected country option's data-currency.
  function syncCurrency() {
    if (!currencyOut || !select) return;
    const opt = select.options[select.selectedIndex];
    if (opt && opt.dataset.currency) currencyOut.textContent = opt.dataset.currency;
  }

  // Triggers are siblings in the header — delegate on document (D25).
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-localization-modal-trigger]');
    if (trigger) {
      e.preventDefault();
      openModal();
    }
  });

  if (closeBtn) closeBtn.addEventListener('click', closeModal);

  // Scrim click closes; clicks inside the panel do not (trigger !== container guard, FAILS §2).
  modal.addEventListener('click', function (e) {
    if (e.target === modal) closeModal();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modal.hasAttribute('data-localization-modal-open')) closeModal();
  });

  // Currency field follows the country select live (display only; never posts currency_code).
  if (select) select.addEventListener('change', syncCurrency);

  // On a market change, drop any PLP price-range filter from the return_to. Range filters live in the
  // URL as f.<id>.min / f.<id>.max on the Voyado market's OWN scale (uk/row/us each differ), so carrying
  // them into a new market filters by numbers that mean nothing there → wrong products. Attribute filters
  // (colour, size), sort and the locale prefix are scale-independent and kept; Shopify swaps the locale
  // on the return_to path we set. Same country selected → keep everything untouched.
  if (select && select.form) {
    select.form.addEventListener('submit', function () {
      if (select.value === initialCountry) return;
      const params = new URLSearchParams(location.search);
      let dropped = false;
      Array.from(params.keys()).forEach(function (key) {
        if (/^f\..+\.(min|max)$/.test(key)) { params.delete(key); dropped = true; }
      });
      if (!dropped) return;
      const qs = params.toString();
      let rt = select.form.querySelector('input[name="return_to"]');
      if (!rt) {
        rt = document.createElement('input');
        rt.type = 'hidden';
        rt.name = 'return_to';
        select.form.appendChild(rt);
      }
      rt.value = location.pathname + (qs ? '?' + qs : '');
    });
  }

  // Enhancement note: the native submit button is the no-JS fallback. The localization
  // form posts country_code and reloads (full-page nav, no fetch), so no AbortController/
  // error-write contract applies; [data-localization-error] is inert in this nav flow.
})();
